import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getCurrentUser, handleSessionExpired, refreshAccessToken } from "../services/auth.service";

/**
 * SessionTimeoutManager
 *
 * Industry-standard inactivity & session timeout manager:
 * - Tracks user activity across keyboard, mouse, touch, and scroll events.
 * - When the user is active, the session stays alive.
 * - If the user is idle for 25 minutes, displays a clear countdown modal warning
 *   giving them 2 minutes to extend their session.
 * - If they click "Stay Signed In", silently refreshes the token and resets the timer.
 * - If the countdown expires without response, logs out gracefully, saves the current page,
 *   and redirects to login.
 */

// Inactivity threshold before warning: 25 minutes
const IDLE_WARNING_MS = 25 * 60 * 1000;
// Countdown duration in warning dialog: 120 seconds (2 minutes)
const WARNING_COUNTDOWN_SECONDS = 120;

const SessionTimeoutManager = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const lastActivityRef = useRef(Date.now());
    const warningOpenRef = useRef(false);
    const countdownIntervalRef = useRef(null);

    // Track user activity (resets idle timer on any real interaction)
    useEffect(() => {
        const handleActivity = () => {
            lastActivityRef.current = Date.now();
        };

        const events = ["mousedown", "keydown", "scroll", "touchstart"];
        events.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

        return () => {
            events.forEach((evt) => window.removeEventListener(evt, handleActivity));
        };
    }, []);

    // Periodic check for idle status
    useEffect(() => {
        const publicPaths = ["/", "/login", "/sign-up"];
        const isPublicPage = publicPaths.includes(location.pathname);

        const checkInterval = setInterval(async () => {
            const user = getCurrentUser();
            // Only monitor if the user is currently logged in on a protected page
            if (!user || !user.token || isPublicPage) {
                return;
            }

            const idleDuration = Date.now() - lastActivityRef.current;

            if (idleDuration >= IDLE_WARNING_MS && !warningOpenRef.current) {
                warningOpenRef.current = true;
                let remainingSeconds = WARNING_COUNTDOWN_SECONDS;

                const swalPromise = Swal.fire({
                    title: "Session Expiring Soon",
                    html: `
                        <p style="margin-bottom: 8px;">You have been inactive for a while.</p>
                        <p style="font-size: 15px; color: #555;">
                            Your session will automatically expire in 
                            <b style="color: #d97706;"><span id="session-timeout-sec">${remainingSeconds}</span></b> seconds.
                        </p>
                        <p style="font-size: 13px; color: #777; margin-top: 10px;">
                            Click <b>Stay Signed In</b> to continue working without interruption.
                        </p>
                    `,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Stay Signed In",
                    cancelButtonText: "Sign Out",
                    confirmButtonColor: "#2b53a3",
                    cancelButtonColor: "#6c757d",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                        const countdownEl = document.getElementById("session-timeout-sec");
                        countdownIntervalRef.current = setInterval(() => {
                            remainingSeconds -= 1;
                            if (countdownEl) {
                                countdownEl.textContent = String(remainingSeconds);
                            }
                            if (remainingSeconds <= 0) {
                                clearInterval(countdownIntervalRef.current);
                                Swal.close();
                                handleSessionExpired();
                            }
                        }, 1000);
                    },
                    willClose: () => {
                        if (countdownIntervalRef.current) {
                            clearInterval(countdownIntervalRef.current);
                        }
                    },
                });

                const result = await swalPromise;
                warningOpenRef.current = false;

                if (result.isConfirmed) {
                    try {
                        await refreshAccessToken();
                        lastActivityRef.current = Date.now();
                        Swal.fire({
                            icon: "success",
                            title: "Session Extended",
                            text: "Your session has been renewed. You can continue your work.",
                            timer: 1500,
                            showConfirmButton: false,
                        });
                    } catch (refreshErr) {
                        console.error("Session renewal failed:", refreshErr);
                        handleSessionExpired();
                    }
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    handleSessionExpired();
                }
            }
        }, 15000); // Check every 15 seconds

        return () => {
            clearInterval(checkInterval);
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [location.pathname, navigate]);

    return null;
};

export default SessionTimeoutManager;

